import app from 'flarum/forum/app';
import { skyspireFixture } from '../data/skyspireFixture';

export const SKYPIRE_DATA_SOURCE: 'mock' | 'sdk' = 'sdk';

export type SkyspireRegion = 'hk' | 'global';

export interface SkyspirePlayer {
  id: string;
  region: SkyspireRegion;
  server: string;
  name: string | null;
  platform: string;
  zoneId: number;
  srvId: string;
  zoneName: string;
  querySuccess: boolean;
  rid: number | null;
  roleSrvId: string | null;
  account: string | null;
  reachTime: number | null;
  reachDurationSeconds: number | null;
  rewardStatus: number | null;
  rewardTime: number | null;
}

export interface SkyspirePageData {
  region: SkyspireRegion;
  page: number;
  pageSize: number;
  totalPages: number;
  players: SkyspirePlayer[];
  latestPlayer: SkyspirePlayer | null;
}

export interface SkyspireSearchResult {
  found: boolean;
  candidates: SkyspireSearchCandidate[];
}

export interface SkyspireSearchCandidate {
  player: SkyspirePlayer;
  region: SkyspireRegion;
  page: number;
  slot: number;
}

export interface SkyspirePageParams {
  region: SkyspireRegion;
  page: number;
  tagId: string | number;
  roleName?: string;
}

const MOCK_PAGE_SIZE = 10;
const REGION_UTC_OFFSET_HOURS: Record<SkyspireRegion, number> = {
  hk: 8,
  global: -8,
};

function padTimePart(value: number): string {
  return String(value).padStart(2, '0');
}

export function formatSkyspireReachTime(reachTime: number | null, region: SkyspireRegion): string {
  if (reachTime == null || !Number.isFinite(reachTime) || reachTime <= 0) return '';

  // Read the shifted instant through UTC getters so browser timezone cannot change the result.
  const date = new Date((reachTime + REGION_UTC_OFFSET_HOURS[region] * 3600) * 1000);
  if (Number.isNaN(date.getTime())) return '';

  return `${date.getUTCFullYear()}.${padTimePart(date.getUTCMonth() + 1)}.${padTimePart(date.getUTCDate())}`;
}

export function getSkyspireElapsedDays(reachDurationSeconds: number | null): number | null {
  if (reachDurationSeconds == null || !Number.isFinite(reachDurationSeconds) || reachDurationSeconds < 0) return null;

  return Math.floor(reachDurationSeconds / 86400);
}

function getMockPageSize(tagId: string | number): number {
  const tag = app.store.getById('tags', String(tagId)) as any;
  const pageSize = Number(tag?.skyspirePageSize?.() || MOCK_PAGE_SIZE);

  return Math.max(1, Math.min(100, pageSize || MOCK_PAGE_SIZE));
}

function mapRecord(record: any, region: SkyspireRegion): SkyspirePlayer {
  const actualRegion = record.skyspireRegion || record.region || region;

  return {
    id: record.srvId || String(record.zoneId),
    region: actualRegion,
    server: record.srvId || '',
    name: record.roleName || null,
    platform: record.platform || '',
    zoneId: Number(record.zoneId),
    srvId: record.srvId || '',
    zoneName: record.zoneName || '',
    querySuccess: Boolean(record.querySuccess),
    rid: record.rid == null ? null : Number(record.rid),
    roleSrvId: record.roleSrvId || null,
    account: record.account || null,
    reachTime: record.reachTime == null ? null : Number(record.reachTime),
    reachDurationSeconds: record.reachDurationSeconds == null ? null : Number(record.reachDurationSeconds),
    rewardStatus: record.rewardStatus == null ? null : Number(record.rewardStatus),
    rewardTime: record.rewardTime == null ? null : Number(record.rewardTime),
  };
}

export function requestSkyspirePage(params: SkyspirePageParams): Promise<SkyspirePageData> {
  const query = new URLSearchParams({
    region: params.region,
    pageNo: String(params.page),
    tagId: String(params.tagId),
  });

  if (params.roleName?.trim()) {
    query.set('roleName', params.roleName.trim());
  }

  return app
    .request({
      method: 'GET',
      url: `${app.forum.attribute('apiUrl')}/seasonTowerFirst?${query.toString()}`,
    })
    .then((response: any) => {
      if (Number(response.ret) !== 0) throw new Error(response.msg || 'SDK request failed');

      const total = Number(response.data?.total || 0);
      const pageSize = Number(response.data?.pageSize || 10);
      const page = Number(response.data?.pageNo || params.page);
      const players = (response.data?.list || []).map((record: any) => mapRecord(record, params.region));
      const latestPlayer = response.data?.lastReachPlayer ? mapRecord(response.data.lastReachPlayer, params.region) : null;

      return {
        region: params.region,
        page,
        pageSize,
        totalPages: Math.max(1, Math.ceil(total / pageSize)),
        players,
        latestPlayer,
      };
    });
}

function loadMockSkyspirePage(params: SkyspirePageParams): Promise<SkyspirePageData> {
  const allPlayers = skyspireFixture[params.region] || [];
  const pageSize = getMockPageSize(params.tagId);
  const totalPages = Math.max(1, Math.ceil(allPlayers.length / pageSize));
  const page = Math.min(Math.max(1, params.page), totalPages);
  const start = (page - 1) * pageSize;
  const players = allPlayers.slice(start, start + pageSize);

  return Promise.resolve({
    region: params.region,
    page,
    pageSize,
    totalPages,
    players,
    latestPlayer: players.find((player) => player.rid !== null) || null,
  });
}

export function loadSkyspirePage(params: SkyspirePageParams): Promise<SkyspirePageData> {
  return SKYPIRE_DATA_SOURCE === 'mock' ? loadMockSkyspirePage(params) : requestSkyspirePage(params);
}

function searchMockSkyspirePlayer(name: string, tagId: string | number, region: SkyspireRegion): Promise<SkyspireSearchResult> {
  const normalizedName = name.trim().toLocaleLowerCase();
  if (!normalizedName) return Promise.resolve({ found: false, candidates: [] });
  const pageSize = getMockPageSize(tagId);
  const candidates: SkyspireSearchCandidate[] = [];

  const players = skyspireFixture[region] || [];
  players.forEach((player, index) => {
    if ((player.name || '').trim().toLocaleLowerCase() !== normalizedName) return;

    candidates.push({
      player,
      region,
      page: Math.floor(index / pageSize) + 1,
      slot: index % pageSize,
    });
  });

  return Promise.resolve({ found: candidates.length > 0, candidates });
}

async function searchSdkSkyspirePlayer(name: string, tagId: string | number, region: SkyspireRegion): Promise<SkyspireSearchResult> {
  const normalizedName = name.trim().toLocaleLowerCase();
  if (!normalizedName) return { found: false, candidates: [] };

  const pageData = await requestSkyspirePage({
    region,
    page: 1,
    tagId,
    roleName: name.trim(),
  });
  const candidates: SkyspireSearchCandidate[] = [];

  pageData.players.forEach((player, slot) => {
    if ((player.name || '').trim().toLocaleLowerCase() !== normalizedName || player.rid === null) {
      return;
    }

    candidates.push({
      player,
      region: player.region,
      page: pageData.page,
      slot,
    });
  });

  return { found: candidates.length > 0, candidates };
}

export function searchSkyspirePlayer(name: string, tagId: string | number, region: SkyspireRegion): Promise<SkyspireSearchResult> {
  return SKYPIRE_DATA_SOURCE === 'mock' ? searchMockSkyspirePlayer(name, tagId, region) : searchSdkSkyspirePlayer(name, tagId, region);
}
